<template>
    <div class="prediction-market-detail">
        <!-- Price History Chart -->
        <div class="compass-card mb-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h5 style="font-weight: 600; margin: 0;">Price History</h5>
                    <div class="text-muted" style="font-size: 0.8rem;">Yes/No probability over time</div>
                </div>
            </div>
            <div v-if="hasHistory" style="height: 250px; position: relative;">
                <canvas ref="priceChart"></canvas>
            </div>
            <div v-else class="text-center py-3 analytics-text-subtle" style="font-size: 0.85rem;">
                <i class="fas fa-chart-line me-1" style="opacity: 0.4;"></i>
                Chart will appear as price data is collected (snapshots every 15 min).
            </div>
        </div>

        <!-- Trading Panel -->
        <div class="row mb-4" v-if="market.status === 'active'">
            <div class="col-lg-7 mb-3">
                <!-- Orderbook -->
                <div class="compass-card">
                    <h5 class="mb-3" style="font-weight: 600;">Orderbook</h5>
                    <div v-if="orderbookLoading" class="text-center py-3">
                        <div class="spinner-border spinner-border-sm text-purple" role="status"></div>
                        <span class="ms-2 text-muted">Loading orderbook...</span>
                    </div>
                    <div v-else-if="orderbook">
                        <div class="row">
                            <!-- Yes (Buy) Side -->
                            <div class="col-6">
                                <div class="mb-2" style="font-size: 0.8rem; font-weight: 600; color: var(--compass-success);">
                                    YES (Buy)
                                </div>
                                <div v-for="(level, i) in orderbookYes" :key="'yes-' + i"
                                     class="d-flex justify-content-between align-items-center mb-1 position-relative"
                                     style="font-size: 0.85rem; padding: 2px 6px; border-radius: 4px;">
                                    <div class="position-absolute" style="left: 0; top: 0; bottom: 0; border-radius: 4px; opacity: 0.1; background: var(--compass-success);"
                                         :style="{ width: (level.quantity / maxYesQty * 100) + '%' }"></div>
                                    <span class="font-mono-num" style="position: relative;">${{ level.price.toFixed(4) }}</span>
                                    <span class="font-mono-num text-muted" style="position: relative;">{{ formatQty(level.quantity) }}</span>
                                </div>
                                <div v-if="!orderbookYes.length" class="text-muted" style="font-size: 0.85rem; padding: 8px 0;">
                                    No orders
                                </div>
                            </div>
                            <!-- No (Buy) Side -->
                            <div class="col-6">
                                <div class="mb-2" style="font-size: 0.8rem; font-weight: 600; color: var(--compass-danger);">
                                    NO (Buy)
                                </div>
                                <div v-for="(level, i) in orderbookNo" :key="'no-' + i"
                                     class="d-flex justify-content-between align-items-center mb-1 position-relative"
                                     style="font-size: 0.85rem; padding: 2px 6px; border-radius: 4px;">
                                    <div class="position-absolute" style="left: 0; top: 0; bottom: 0; border-radius: 4px; opacity: 0.1; background: var(--compass-danger);"
                                         :style="{ width: (level.quantity / maxNoQty * 100) + '%' }"></div>
                                    <span class="font-mono-num" style="position: relative;">${{ level.price.toFixed(4) }}</span>
                                    <span class="font-mono-num text-muted" style="position: relative;">{{ formatQty(level.quantity) }}</span>
                                </div>
                                <div v-if="!orderbookNo.length" class="text-muted" style="font-size: 0.85rem; padding: 8px 0;">
                                    No orders
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="py-3 text-center text-muted" style="font-size: 0.9rem;">
                        Orderbook not available for this market.
                    </div>
                </div>
            </div>

            <div class="col-lg-5 mb-3">
                <!-- Buy/Sell Form -->
                <div class="compass-card compass-card--accent">
                    <h5 class="mb-3" style="font-weight: 600;">Trade</h5>

                    <!-- Outcome Toggle -->
                    <div class="analytics-tabs mb-3">
                        <button class="tab-btn" :class="{ active: tradeOutcome === 'yes' }" @click="tradeOutcome = 'yes'"
                                style="flex: 1;">
                            <span style="color: var(--compass-success);">Yes</span>
                            <span class="font-mono-num ms-1">${{ Number(market.yes_price).toFixed(2) }}</span>
                        </button>
                        <button class="tab-btn" :class="{ active: tradeOutcome === 'no' }" @click="tradeOutcome = 'no'"
                                style="flex: 1;">
                            <span style="color: var(--compass-danger);">No</span>
                            <span class="font-mono-num ms-1">${{ Number(market.no_price).toFixed(2) }}</span>
                        </button>
                    </div>

                    <!-- Amount Input -->
                    <div class="mb-3">
                        <label class="text-muted" style="font-size: 0.85rem; font-weight: 500;">Amount (USDC)</label>
                        <div class="input-group">
                            <input v-model.number="tradeAmount" type="number" class="form-control" min="0.01" step="0.01"
                                   placeholder="10.00" style="font-family: var(--compass-font-mono, monospace);">
                            <span class="input-group-text">USDC</span>
                        </div>
                        <div class="d-flex gap-2 mt-2">
                            <button v-for="amt in [1, 5, 10, 25, 50, 100]" :key="amt"
                                    class="compass-btn compass-btn--sm compass-btn--outline"
                                    style="font-size: 0.75rem; padding: 2px 8px;"
                                    @click="tradeAmount = amt">
                                ${{ amt }}
                            </button>
                        </div>
                    </div>

                    <!-- Max Price (optional) -->
                    <div class="mb-3">
                        <label class="text-muted" style="font-size: 0.85rem; font-weight: 500;">Max Price (optional)</label>
                        <input v-model.number="tradeMaxPrice" type="number" class="form-control form-control-sm"
                               min="0.01" max="0.99" step="0.01" placeholder="0.85"
                               style="font-family: var(--compass-font-mono, monospace);">
                        <div class="mt-1 analytics-text-subtle" style="font-size: 0.75rem;">
                            Leave empty for market order. Set a max price for limit protection.
                        </div>
                    </div>

                    <!-- Estimated Shares -->
                    <div v-if="tradeAmount" class="compass-info-box mb-3" style="padding: 0.75rem;">
                        <div class="d-flex justify-content-between" style="font-size: 0.85rem;">
                            <span class="text-muted">Est. shares</span>
                            <span class="font-mono-num">~{{ estimatedShares }}</span>
                        </div>
                        <div class="d-flex justify-content-between" style="font-size: 0.85rem;">
                            <span class="text-muted">If {{ tradeOutcome }} wins</span>
                            <span class="font-mono-num" style="color: var(--compass-success);">
                                ${{ estimatedPayout }}
                            </span>
                        </div>
                    </div>

                    <!-- Buy Button -->
                    <button class="compass-btn compass-btn--primary w-100"
                            :disabled="!tradeAmount || tradeAmount <= 0 || tradePending"
                            @click="executeBuy">
                        <span v-if="tradePending">
                            <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                            Processing...
                        </span>
                        <span v-else>
                            Buy {{ capitalize(tradeOutcome) }} — ${{ tradeAmount || 0 }} USDC
                        </span>
                    </button>

                    <div v-if="tradeError" class="mt-2" style="font-size: 0.85rem; color: var(--compass-danger);">
                        {{ tradeError }}
                    </div>
                    <div v-if="tradeSuccess" class="mt-2" style="font-size: 0.85rem; color: var(--compass-success);">
                        {{ tradeSuccess }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Resolved Market Banner -->
        <div v-if="market.status === 'resolved'" class="compass-info-box mb-4"
             :class="market.resolution === 'yes' ? 'compass-info-box--success' : 'compass-info-box--danger'">
            <h5>Market Resolved: {{ capitalize(market.resolution || 'Unknown') }}</h5>
            <p class="mb-0">
                This market has been settled. Winning shares paid out $1.00 each.
                If you hold winning positions, you can claim your payout.
            </p>
        </div>

        <!-- User's Position in This Market -->
        <div v-if="userPosition" class="compass-card compass-card--accent mb-4">
            <h5 class="mb-2" style="font-weight: 600;">Your Position</h5>
            <div class="row">
                <div class="col-md-3 col-6 mb-2">
                    <div class="text-muted" style="font-size: 0.8rem;">Side</div>
                    <div :style="{ fontWeight: 600, color: userPosition.isYes ? 'var(--compass-success)' : 'var(--compass-danger)' }">
                        {{ userPosition.isYes ? 'Yes' : 'No' }} &times;{{ userPosition.contracts }}
                    </div>
                </div>
                <div class="col-md-3 col-6 mb-2">
                    <div class="text-muted" style="font-size: 0.8rem;">Avg Price</div>
                    <div class="font-mono-num">${{ formatPrice(userPosition.avgPriceUsd) }}</div>
                </div>
                <div class="col-md-3 col-6 mb-2">
                    <div class="text-muted" style="font-size: 0.8rem;">Cost Basis</div>
                    <div class="font-mono-num">${{ formatPrice(userPosition.costBasisUsd) }}</div>
                </div>
                <div class="col-md-3 col-6 mb-2">
                    <div class="text-muted" style="font-size: 0.8rem;">P&L</div>
                    <div class="font-mono-num" :class="(userPosition.unrealizedPnlPct || 0) >= 0 ? 'text-success' : 'text-danger'">
                        {{ (userPosition.unrealizedPnlPct || 0) >= 0 ? '+' : '' }}{{ formatPct(userPosition.unrealizedPnlPct) }}
                        (${{ formatPrice(userPosition.unrealizedPnlUsd) }})
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useSolSdk } from '../composables/useSolSdk';
import { formatPct, formatPrice, capitalize } from '../composables/usePredictFormatters';

export default {
    name: 'PredictionMarketDetail',
    props: {
        market: { type: Object, required: true },
        marketId: { type: String, required: true },
    },
    setup(props) {
        const { getSdk } = useSolSdk();

        // Orderbook state
        const orderbook = ref(null);
        const orderbookLoading = ref(false);

        // Chart state
        const priceChart = ref(null);
        const historyData = ref([]);
        const hasHistory = computed(() => historyData.value.length > 1);
        let chartInstance = null;

        const orderbookYes = computed(() => {
            if (!orderbook.value?.yes) return [];
            return orderbook.value.yes.slice(0, 10);
        });

        const orderbookNo = computed(() => {
            if (!orderbook.value?.no) return [];
            return orderbook.value.no.slice(0, 10);
        });

        const maxYesQty = computed(() => {
            if (!orderbookYes.value.length) return 1;
            return Math.max(...orderbookYes.value.map(l => l.quantity));
        });

        const maxNoQty = computed(() => {
            if (!orderbookNo.value.length) return 1;
            return Math.max(...orderbookNo.value.map(l => l.quantity));
        });

        // Trade state
        const tradeOutcome = ref('yes');
        const tradeAmount = ref(null);
        const tradeMaxPrice = ref(null);
        const tradePending = ref(false);
        const tradeError = ref('');
        const tradeSuccess = ref('');

        // Position state
        const userPosition = ref(null);

        const currentPrice = computed(() => {
            return tradeOutcome.value === 'yes' ? props.market.yes_price : props.market.no_price;
        });

        const estimatedShares = computed(() => {
            if (!tradeAmount.value || !currentPrice.value) return '0';
            return (tradeAmount.value / currentPrice.value).toFixed(2);
        });

        const estimatedPayout = computed(() => {
            if (!tradeAmount.value || !currentPrice.value) return '0.00';
            const shares = tradeAmount.value / currentPrice.value;
            return shares.toFixed(2);
        });



        function formatQty(val) {
            if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
            return Number(val).toFixed(0);
        }

        async function loadPriceHistory() {
            try {
                const resp = await fetch('/api/predict/markets/' + props.marketId + '/history');
                const json = await resp.json();
                historyData.value = json.data || [];
                nextTick(() => renderChart());
            } catch (e) {
                console.error('[PredictionMarketDetail] Failed to load price history:', e);
            }
        }

        function renderChart() {
            if (!priceChart.value) return;
            if (chartInstance) {
                chartInstance.destroy();
                chartInstance = null;
            }

            const data = historyData.value;
            if (data.length < 2) return;

            const labels = data.map(d => {
                const dt = new Date(d.time);
                return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
                       ' ' + dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
            });

            const Chart = window.Chart;
            if (!Chart) return;

            chartInstance = new Chart(priceChart.value.getContext('2d'), {
                type: 'line',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'Yes',
                            data: data.map(d => (d.yes * 100).toFixed(1)),
                            borderColor: '#22c55e',
                            backgroundColor: 'rgba(34, 197, 94, 0.08)',
                            fill: true,
                            tension: 0.4,
                            pointRadius: data.length > 20 ? 0 : 2,
                            pointHoverRadius: 5,
                            borderWidth: 2,
                        },
                        {
                            label: 'No',
                            data: data.map(d => (d.no * 100).toFixed(1)),
                            borderColor: '#ef4444',
                            backgroundColor: 'rgba(239, 68, 68, 0.05)',
                            fill: true,
                            tension: 0.4,
                            pointRadius: data.length > 20 ? 0 : 2,
                            pointHoverRadius: 5,
                            borderWidth: 2,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {
                        legend: {
                            display: true,
                            position: 'top',
                            labels: {
                                usePointStyle: true,
                                pointStyle: 'circle',
                                font: { size: 12 },
                            },
                        },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => ctx.dataset.label + ': ' + ctx.parsed.y + '%',
                            },
                        },
                    },
                    scales: {
                        x: {
                            display: true,
                            grid: { display: false },
                            ticks: {
                                maxRotation: 0,
                                maxTicksLimit: 6,
                                font: { size: 11 },
                            },
                        },
                        y: {
                            min: 0,
                            max: 100,
                            grid: {
                                color: 'rgba(139, 92, 246, 0.06)',
                            },
                            ticks: {
                                callback: (val) => val + '%',
                                font: { size: 11 },
                            },
                        },
                    },
                },
            });
        }

        async function loadOrderbook() {
            orderbookLoading.value = true;
            try {
                const sdk = await getSdk();
                const result = await sdk.predict.getOrderbook(props.marketId);
                orderbook.value = result;
            } catch (e) {
                console.error('[PredictionMarketDetail] Failed to load orderbook:', e);
                orderbook.value = null;
            } finally {
                orderbookLoading.value = false;
            }
        }

        async function loadPosition() {
            const wallet = window.wallet;
            const pubkey = wallet?.publicKey?.value;
            if (!pubkey) {
                userPosition.value = null;
                return;
            }

            try {
                const sdk = await getSdk();
                const positions = await sdk.predict.getPositions(pubkey.toBase58());
                userPosition.value = (positions || []).find(
                    p => p.marketId === props.marketId
                ) || null;
            } catch (e) {
                console.error('[PredictionMarketDetail] Failed to load position:', e);
                userPosition.value = null;
            }
        }

        async function executeBuy() {
            const wallet = window.wallet;
            const pubkey = wallet?.publicKey?.value;
            if (!pubkey) {
                tradeError.value = 'Please connect your wallet first.';
                return;
            }

            tradePending.value = true;
            tradeError.value = '';
            tradeSuccess.value = '';

            try {
                const sdk = await getSdk();
                const result = await sdk.predict.buy(
                    pubkey.toBase58(),
                    props.marketId,
                    tradeOutcome.value === 'yes',
                    tradeAmount.value,
                    tradeMaxPrice.value || undefined
                );

                tradeSuccess.value = `Bought ${result.contracts} shares at $${Number(result.priceUsd).toFixed(4)}`;
                tradeAmount.value = null;
                tradeMaxPrice.value = null;

                // Refresh position and orderbook
                loadPosition();
                loadOrderbook();
            } catch (e) {
                console.error('[PredictionMarketDetail] Buy failed:', e);
                tradeError.value = e.message || 'Transaction failed. Please try again.';
            } finally {
                tradePending.value = false;
            }
        }

        onMounted(() => {
            loadPriceHistory();
            loadOrderbook();
            if (window.wallet?.publicKey?.value) {
                loadPosition();
            }
        });

        // Watch for wallet connection changes
        if (window.wallet?.publicKey) {
            watch(() => window.wallet.publicKey.value, (newVal) => {
                if (newVal) loadPosition();
                else userPosition.value = null;
            });
        }

        return {
            orderbook,
            orderbookLoading,
            orderbookYes,
            orderbookNo,
            maxYesQty,
            maxNoQty,
            tradeOutcome,
            tradeAmount,
            tradeMaxPrice,
            tradePending,
            tradeError,
            tradeSuccess,
            userPosition,
            estimatedShares,
            estimatedPayout,
            priceChart,
            hasHistory,
            capitalize,
            formatPrice,
            formatPct,
            formatQty,
            executeBuy,
        };
    },
};
</script>
